import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { eCourseService } from "../../services/elearning/eCourseService";
import {
  DollarSign,
  Users,
  TrendingUp,
  BookOpen,
  Download,
  Star,
  Calendar,
  AlertCircle,
  GraduationCap,
  Target,
} from "lucide-react";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";
import Pagination from "../../components/common/Pagination";

interface CourseAnalytics {
  _id: string;
  title: string;
  teacher: {
    name: string;
  };
  enrollments: number;
  completions: number;
  completionRate: number;
  revenue: number;
  averageRating: number;
  totalRatings: number;
  status: string;
  createdAt: string;
}

const AdminECourseAnalytics: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["courseAnalytics", currentPage, pageSize],
    queryFn: () => eCourseService.getCoursesAnalytics(currentPage, pageSize),
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleExport = async () => {
    try {
      // This would call an export endpoint if implemented
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
            <p className="text-slate-600 font-medium">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-4 w-fit mx-auto mb-4">
              <AlertCircle className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              Error loading analytics
            </h3>
            <p className="text-slate-600 font-medium mt-2">
              {error instanceof Error
                ? error.message
                : "Failed to load analytics data"}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-6 bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <AlertCircle className="h-5 w-5 text-blue-500" />
              <span className="text-slate-700">Retry</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const analytics = data?.data || [];
  const summary = data?.summary || {
    totalRevenue: 0,
    totalEnrollments: 0,
    averageCompletionRate: 0,
    totalCourses: 0,
  };
  const pagination = data?.pagination;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-3 shadow-lg">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                E-Learning Course Analytics
              </h1>
              <p className="text-slate-600 font-medium mt-1">
                Track course performance and revenue
              </p>
            </div>
          </div>
          <button
            onClick={handleExport}
            className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
          >
            <Download className="h-5 w-5 text-blue-500" />
            <span className="text-slate-700">Export Data</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Revenue</p>
              <p className="text-2xl font-bold text-slate-900 mt-2">
                {formatCurrency(summary.totalRevenue)}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-2 shadow-lg">
              <DollarSign className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">
                Total Enrollments
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-2">
                {summary.totalEnrollments}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-2 shadow-lg">
              <Users className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">
                Avg Completion Rate
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-2">
                {summary.averageCompletionRate.toFixed(1)}%
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-2 shadow-lg">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Courses</p>
              <p className="text-2xl font-bold text-slate-900 mt-2">
                {summary.totalCourses}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-2 shadow-lg">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Courses Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200/60 p-6">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2.5 shadow-lg">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">
              Course Performance Details
            </h2>
          </div>
        </div>
        <div ref={tableScrollRef} className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-blue-500" />
                    Course Title
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4 text-purple-500" />
                    Teacher
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-emerald-500" />
                    Status
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-500" />
                    Enrollments
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-emerald-500" />
                    Completions
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-purple-500" />
                    Rate
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-emerald-500" />
                    Revenue
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    Rating
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    Created
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {analytics.map((course) => (
                <tr
                  key={course._id}
                  className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all"
                >
                  <td className="px-6 py-4 text-sm font-bold text-slate-900">
                    {course.title}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-700">
                    {course.teacher?.name || "N/A"}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-3 py-1.5 text-xs font-bold rounded-xl text-white shadow-sm ${
                        course.status === "published"
                          ? "bg-gradient-to-br from-emerald-300 to-green-400"
                          : "bg-gradient-to-br from-slate-300 to-slate-400"
                      }`}
                    >
                      {course.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-900">
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-br from-blue-300 to-blue-400 rounded-lg p-1.5">
                        <Users className="h-3.5 w-3.5 text-white" />
                      </div>
                      {course.enrollments}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-900">
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-lg p-1.5">
                        <Target className="h-3.5 w-3.5 text-white" />
                      </div>
                      {course.completions}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-900">
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-1.5">
                        <TrendingUp className="h-3.5 w-3.5 text-white" />
                      </div>
                      {course.completionRate.toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-900">
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-lg p-1.5">
                        <DollarSign className="h-3.5 w-3.5 text-white" />
                      </div>
                      {formatCurrency(course.revenue)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-900">
                    {course.averageRating > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="bg-gradient-to-br from-amber-300 to-yellow-400 rounded-lg p-1.5">
                          <Star className="h-3.5 w-3.5 text-white" />
                        </div>
                        <span>
                          {course.averageRating.toFixed(1)} (
                          {course.totalRatings})
                        </span>
                      </div>
                    ) : (
                      <span className="text-slate-500 font-medium">
                        No ratings
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      {formatDate(course.createdAt)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="px-6 py-4 border-t border-slate-200">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminECourseAnalytics;
